import axios from "axios";

import { User } from "model/user";
import { TokenResponse } from "@react-oauth/google";
import { AccountAttribution } from "model/account";

const baseUrlStage = "https://api-beta.onelink.to/";
const baseUrlProd = "https://api.onelink.to/";
const baseUrlDev = "http://localhost:8000/";

const buildenv = process.env.REACT_APP_BUILD_ENV;
const baseUrl =
  buildenv === "production"
    ? baseUrlProd
    : buildenv === "production-test"
      ? baseUrlProd
      : buildenv === "stage"
        ? baseUrlStage
        : baseUrlDev;

///////////////////////////////////////////////////////////////////////////
const getSendHeaders = (
  user?: User,
  contentType: string = "application/json"
) => {
  return getHeaders(user, contentType);
};

const getHeaders = (user?: User, contentType?: string) => {
  const headers: { [name: string]: string } = {};
  if (user) headers["Access-Token"] = user.accessToken || "";
  if (contentType) headers["Content-Type"] = contentType;
  return headers;
};

const axiosPost = (path: string, data?: any, user?: User) => {
  return axios.post(`${baseUrl}${path}`, data && JSON.stringify(data), {
    headers: getSendHeaders(user),
  });
};
const axiosPut = (path: string, data?: any, user?: User) => {
  return axios.put(`${baseUrl}${path}`, data && JSON.stringify(data), {
    headers: getSendHeaders(user),
  });
};

const axiosDelete = (path: string, user?: User) => {
  return axios.delete(`${baseUrl}${path}`, {
    headers: getHeaders(user),
  });
};

const axiosGet = (
  path: string,
  user?: User,
  params?: { [name: string]: string | number }
) => {
  return axios.get(`${baseUrl}${path}`, {
    headers: getHeaders(user),
    params,
  });
};

///////////////////////////////////////////////////////////////////////////

export const apiRegisterGoogle = (
  tokenResponse: TokenResponse,
  attribution: AccountAttribution,
  invitationToken?: string
) => {
  const data = {
    accessToken: tokenResponse.access_token,
    invitationToken: invitationToken,
    attribution: attribution
  };
  return axiosPost("auth/register-google", data);
};

export const apiSignInGoogle = (tokenResponse: TokenResponse) => {
  const data = {
    accessToken: tokenResponse.access_token,
  };
  return axiosPost("auth/signin-google", data);
};

export const apiRegisterEmailPassword = (
  email: string,
  password: string,
  attribution: AccountAttribution,
  invitationToken?: string
) => {
  const data = {
    email,
    password,
    invitationToken: invitationToken,
    website: window.location.origin,
    attribution: attribution
  };
  return axiosPost("auth/register-email-password", data);
};

export const apiSignInEmailPassword = (email: string, password: string) => {
  const data = {
    email,
    password,
  };
  return axiosPost("auth/signin-email-password", data);
};

export const apiSignInAccessToken = (accessToken: string) => {
  const user = { accessToken };
  return axiosPost("auth/signin-access-token", null, user);
};

export const apiTriggerPasswordReset = (email: string) => {
  const data = {
    email: email.trim().toLowerCase(),
    website: window.location.origin,
  };
  return axiosPost("auth/trigger-reset-password", data);
};

export const apiResetPassword = (
  email: string,
  token: string,
  newPassword: string
) => {
  const data = {
    email: email.trim().toLowerCase(),
    token,
    newPassword,
  };
  return axiosPut("auth/reset-password", data);
};

///////////////////////////////////////////////////////////////////////////

export const apiAccountUpdate = (user: User, data: any) => {
  return axiosPut("account/", data, user);
};

export const apiAccountTransferLegacy = (user: User) => {
  return axiosPost("account/legacy-transfer", null, user);
};

export const apiAccountInvite = (user: User, email: string) => {
  const data = {
    email: email.trim().toLowerCase(),
    website: window.location.origin,
  };
  return axiosPost("account/invite", data, user);
};

export const apiGetInvitationList = (user: User) => {
  return axiosGet("account/invitations", user);
};

export const apiGetInvitationInfo = (token: string) => {
  return axiosGet("account/invitation/" + token);
};
export const apiDeleteInvitation = (user: User, token: string) => {
  return axiosDelete("account/invitation/" + token, user);
};

export const apiGetUserList = (user: User) => {
  return axiosGet("account/users", user);
};
export const apiDeleteUser = (user: User, uuid: string) => {
  return axiosDelete("account/user/" + uuid, user);
};

export const apiViesCheckVat = (vatNo: string, user: User) => {
  return axiosGet("account/vat/check/" + vatNo, user);
};

export const apiAccountDelete = (user: User, uuid: string) => {
  return axiosDelete("account/" + uuid, user);
};

///////////////////////////////////////////////////////////////////////////

export const apiLoadOnelinks = (user: User) => {
  return axiosGet("onelink/list", user);
};

export const apiNewOnelink = (user: User) => {
  return axiosPost("onelink/", null, user);
};

export const apiUpdateOnelink = (user: User, onelinkId: string, data: any) => {
  return axiosPut("onelink/" + onelinkId, data, user);
};

export const apiDeleteOnelink = (user: User, onelinkId: string) => {
  return axiosDelete("onelink/" + onelinkId, user);
};

export const apiValidateOnelinkCustomId = (user: User, customId: string) => {
  return axiosGet("onelink/check-custom/" + customId, user);
};

///////////////////////////////////////////////////////////////////////////

export const apiTriggerEmailVerification = (email: string) => {
  const data = {
    email: email.trim().toLowerCase(),
    website: window.location.origin,
  };
  return axiosPost("user/trigger-email-verification", data);
};

export const apiVerifyEmail = (token: string) => {
  const data = {
    code: token,
  };
  return axiosPut("user/verify-email", data);
};

export const apiChangePassword = (
  user: User,
  currentPassword: string,
  newPassword: string
) => {
  const data = {
    currentPassword,
    newPassword,
  };
  return axiosPut("user/change-password", data, user);
};

export const apiChangeEmail = (user: User, email: string) => {
  const data = {
    email,
    website: window.location.origin,
  };
  return axiosPut("user/change-email", data, user);
};

///////////////////////////////////////////////////////////////////////////

export const apiOnelinkStats = (
  user: User,
  onelinkId: string,
  type: string,
  fromUtc?: number,
  throughUtc?: number
) => {
  const params: { [name: string]: string | number } = {};
  if (fromUtc) params["from_date"] = fromUtc;
  if (throughUtc) params["through_date"] = throughUtc;
  return axiosGet("onelink/stats/" + onelinkId + "/" + type, user, params);
};

///////////////////////////////////////////////////////////////////////////

export const apiSetupSubscription = (user: User) => {
  return axiosPost("payment/setup-subscription", null, user);
};

export const apiGetPaymentCustomerPortalUrl = (user: User) => {
  return axiosGet("payment/customer-portal-url", user);
};

export const apiGetSubscriptionStatus = (user: User) => {
  return axiosGet("payment/subscription-status", user);
};

export const apiPutCancelSubscription = (user: User) => {
  return axiosPut("payment/cancel-subscription", null, user);
};

///////////////////////////////////////////////////////////////////////////
